import type { UserRole } from '@/types/models';

/**
 * Master data screens are described, not hand-written. Nine near-identical
 * CRUD pages would be nine places for a bug to hide; one EntityManager
 * driven by these descriptors is one.
 *
 * Adding a lookup table to the admin area is an entry here, nothing more.
 */

export type FieldType = 'text' | 'textarea' | 'number' | 'boolean' | 'select' | 'time' | 'color';

export interface FieldDef {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  help?: string;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
  full?: boolean;
  options?: { value: string; label: string }[];
  /** Foreign key: pull options from another table in the local mirror. */
  lookup?: { table: string; label: string };
  section?: string;
  default?: unknown;
}

export interface ColumnDef {
  key: string;
  label: string;
  align?: 'left' | 'right' | 'center';
  type?: 'text' | 'number' | 'boolean' | 'colour';
  decimals?: number;
  lookup?: { table: string; label: string };
}

export interface EntityDef {
  key: string;
  table: string;
  label: string;
  singular: string;
  description: string;
  group: string;
  orderBy: string;
  columns: ColumnDef[];
  fields: FieldDef[];
  editableBy: UserRole[];
  /** Columns every row of this list has (shown rows filter on them, saves set them). */
  fixed?: Record<string, unknown>;
}

const ACTIVE_FIELD: FieldDef = {
  name: 'is_active', label: 'Active', type: 'boolean', default: true,
  help: 'Inactive records stay for history but are hidden from selection lists.',
};

const CODE_FIELD: FieldDef = {
  name: 'code', label: 'Code', type: 'text', required: true,
  help: 'Short unique key. Used in reports and imports — avoid changing it later.',
};

const MANAGER: UserRole[] = ['manager', 'admin'];

export const ENTITIES: EntityDef[] = [
  // ------------------------------------------------------------ Fleet
  {
    key: 'machine-types',
    table: 'machine_types',
    label: 'Machine types',
    singular: 'Machine type',
    group: 'Fleet',
    description:
      'The categories the fleet is grouped by. The flags decide which activities a machine may perform and whether it tracks liner life.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'is_hauling', label: 'Hauling', type: 'boolean', align: 'center' },
      { key: 'is_loading', label: 'Loading', type: 'boolean', align: 'center' },
      { key: 'is_plant', label: 'Plant', type: 'boolean', align: 'center' },
      { key: 'tracks_liners', label: 'Liners', type: 'boolean', align: 'center' },
      { key: 'default_expected_hours', label: 'Hrs/day', type: 'number', decimals: 1, align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'default_expected_hours', label: 'Expected hours per day', type: 'number',
        min: 0, max: 24, step: 0.5, suffix: 'h', default: 10,
        help: 'Default scheduled hours for a new machine of this type.' },
      { name: 'sort_order', label: 'Sort order', type: 'number', step: 1, default: 0 },
      { name: 'is_hauling', label: 'Hauling machine', type: 'boolean', section: 'Behaviour',
        help: 'ADTs and dump trucks. Their loads become production tonnes.' },
      { name: 'is_loading', label: 'Loading machine', type: 'boolean',
        help: 'Excavators and loaders. They feed haulers, so their loads are not counted again.' },
      { name: 'is_plant', label: 'Fixed or mobile plant', type: 'boolean',
        help: 'Crushers, screens, Barmac.' },
      { name: 'tracks_liners', label: 'Tracks liners and wear parts', type: 'boolean',
        help: 'Enables the liner register for machines of this type.' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },

  // ------------------------------------------------- Diary vocabulary
  {
    key: 'time-categories',
    table: 'time_categories',
    label: 'Time categories',
    singular: 'Time category',
    group: 'Daily diary',
    description:
      'How a shift is accounted for. These flags drive availability and utilisation directly — change them only with the calculation in mind.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'is_productive', label: 'Productive', type: 'boolean', align: 'center' },
      { key: 'is_standby', label: 'Standby', type: 'boolean', align: 'center' },
      { key: 'is_downtime', label: 'Downtime', type: 'boolean', align: 'center' },
      { key: 'is_planned', label: 'Planned', type: 'boolean', align: 'center' },
      { key: 'is_weather', label: 'Weather', type: 'boolean', align: 'center' },
      { key: 'colour', label: 'Colour', type: 'colour', align: 'center' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'sort_order', label: 'Sort order', type: 'number', step: 1, default: 0 },
      { name: 'colour', label: 'Colour', type: 'color', help: 'Used on the diary time bar.' },
      { name: 'is_productive', label: 'Productive time', type: 'boolean', section: 'Classification',
        help: 'Counts toward utilisation. Exactly one of productive, standby or downtime must be set.' },
      { name: 'is_standby', label: 'Standby (available but idle)', type: 'boolean',
        help: 'The machine could have worked. This is what separates low utilisation from low availability.' },
      { name: 'is_downtime', label: 'Downtime (could not work)', type: 'boolean' },
      { name: 'is_planned', label: 'Planned maintenance or service', type: 'boolean', section: 'Downtime detail',
        help: 'Excluded from availability by default — see Settings.' },
      { name: 'is_weather', label: 'Weather', type: 'boolean',
        help: 'Environmental, not a machine fault. Excluded from availability by default.' },
      { name: 'is_breakdown', label: 'Requires a breakdown record', type: 'boolean',
        help: 'Selecting this category in the diary prompts for breakdown details.' },
      { name: 'requires_reason', label: 'Requires a reason', type: 'boolean' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'activity-types',
    table: 'activity_types',
    label: 'Activity types',
    singular: 'Activity type',
    group: 'Daily diary',
    description:
      'What a machine was doing. The flags decide what becomes production tonnes and what draws down blast stock.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'captures_loads', label: 'Loads', type: 'boolean', align: 'center' },
      { key: 'is_haulage', label: 'Production', type: 'boolean', align: 'center' },
      { key: 'consumes_blast_stock', label: 'Blast stock', type: 'boolean', align: 'center' },
      { key: 'default_unit', label: 'Unit' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'default_unit', label: 'Unit', type: 'select', default: 'loads',
        options: [
          { value: 'loads', label: 'Loads' },
          { value: 'tonnes', label: 'Tonnes' },
          { value: 'm3', label: 'Cubic metres' },
          { value: 'hours', label: 'Hours' },
          { value: 'none', label: 'No quantity' },
        ] },
      { name: 'sort_order', label: 'Sort order', type: 'number', step: 1, default: 0 },
      { name: 'captures_loads', label: 'Captures a load count', type: 'boolean', section: 'Behaviour' },
      { name: 'is_haulage', label: 'Counts as quarry production', type: 'boolean',
        help: 'Only haulage counts, so an excavator loading an ADT is not counted twice.' },
      { name: 'consumes_blast_stock', label: 'Draws down blast stock', type: 'boolean',
        help: 'Deducts from the selected blast and feeds the depletion forecast.' },
      { name: 'production_on_hauling_only', label: 'Counts only on a hauling machine', type: 'boolean',
        help: 'Production and blast draw-down count only when an ADT or dump truck records it. A loader recording the same loads keeps the hours, not the tonnes.' },
      { name: 'requires_blast', label: 'Blast reference required', type: 'boolean' },
      { name: 'requires_destination', label: 'Destination required', type: 'boolean' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'shifts',
    table: 'shifts',
    label: 'Shifts',
    singular: 'Shift',
    group: 'Daily diary',
    description: 'Scheduled hours per shift. This is the denominator of the availability calculation.',
    orderBy: 'sort_order',
    columns: [
      { key: 'site_id', label: 'Site', lookup: { table: 'sites', label: 'name' } },
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'start_time', label: 'Start' },
      { key: 'end_time', label: 'End' },
      { key: 'scheduled_hours', label: 'Scheduled', type: 'number', decimals: 1, align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      { name: 'site_id', label: 'Site', type: 'select', required: true,
        lookup: { table: 'sites', label: 'name' },
        help: 'The quarry or branch this belongs to. Diaries offer only their own site\'s list.' },
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'start_time', label: 'Start time', type: 'time' },
      { name: 'end_time', label: 'End time', type: 'time' },
      { name: 'scheduled_hours', label: 'Scheduled hours', type: 'number', required: true,
        min: 0.5, max: 24, step: 0.5, suffix: 'h', default: 10,
        help: 'Diary entries must reconcile to this.' },
      { name: 'sort_order', label: 'Sort order', type: 'number', step: 1, default: 0 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'operators',
    table: 'operators',
    label: 'Operators',
    singular: 'Operator',
    group: 'Daily diary',
    description: 'People who operate machines. They do not need a login to appear on a diary entry.',
    orderBy: 'full_name',
    columns: [
      { key: 'site_id', label: 'Site', lookup: { table: 'sites', label: 'name' } },
      { key: 'employee_no', label: 'Employee no.' },
      { key: 'full_name', label: 'Name' },
      { key: 'licence_class', label: 'Licence' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      { name: 'site_id', label: 'Site', type: 'select', required: true,
        lookup: { table: 'sites', label: 'name' },
        help: 'The quarry or branch this belongs to. Diaries offer only their own site\'s list.' },
      { name: 'full_name', label: 'Full name', type: 'text', required: true },
      { name: 'employee_no', label: 'Employee number', type: 'text' },
      { name: 'licence_class', label: 'Licence class', type: 'text' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },

  // ---------------------------------------------------------- Material
  {
    key: 'materials',
    table: 'materials',
    label: 'Materials',
    singular: 'Material',
    group: 'Material & places',
    description: 'Density converts blasted volume to tonnes. Get this wrong and every blast figure is wrong.',
    orderBy: 'code',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'density_t_m3', label: 'Density t/m³', type: 'number', decimals: 3, align: 'right' },
      { key: 'is_waste', label: 'Waste', type: 'boolean', align: 'center' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'density_t_m3', label: 'Density', type: 'number', required: true,
        min: 0.1, max: 10, step: 0.001, suffix: 't/m³', default: 2.5,
        help: 'Blasted tonnes = volume × density.' },
      { name: 'is_waste', label: 'Waste or overburden', type: 'boolean',
        help: 'Waste movement is tracked but does not count as saleable production.' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'products',
    table: 'products',
    label: 'Products',
    singular: 'Product',
    group: 'Material & places',
    description: 'Saleable products and intermediate streams.',
    orderBy: 'code',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'size_mm', label: 'Size' },
      { key: 'material_id', label: 'Material', lookup: { table: 'materials', label: 'name' } },
      { key: 'is_saleable', label: 'Saleable', type: 'boolean', align: 'center' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'size_mm', label: 'Size', type: 'text', help: 'e.g. 19, 13, 0-5' },
      { name: 'material_id', label: 'Material', type: 'select', lookup: { table: 'materials', label: 'name' } },
      { name: 'is_saleable', label: 'Saleable', type: 'boolean', default: true },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'locations',
    table: 'locations',
    label: 'Locations',
    singular: 'Location',
    group: 'Material & places',
    description: 'Benches, faces, stockpiles, crusher, waste dump — the sources and destinations on a diary entry.',
    orderBy: 'code',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'kind', label: 'Kind' },
      { key: 'bench', label: 'Bench' },
      { key: 'area', label: 'Area' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'kind', label: 'Kind', type: 'select', required: true, default: 'other',
        options: [
          { value: 'bench', label: 'Bench' },
          { value: 'face', label: 'Face' },
          { value: 'stockpile', label: 'Stockpile' },
          { value: 'crusher', label: 'Crusher' },
          { value: 'screen', label: 'Screen' },
          { value: 'waste_dump', label: 'Waste dump' },
          { value: 'workshop', label: 'Workshop' },
          { value: 'customer', label: 'Customer' },
          { value: 'other', label: 'Other' },
        ] },
      { name: 'bench', label: 'Bench', type: 'text' },
      { name: 'area', label: 'Area', type: 'text' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },

  // ------------------------------------------------------- Maintenance
  {
    key: 'breakdown-categories',
    table: 'breakdown_categories',
    label: 'Breakdown categories',
    singular: 'Breakdown category',
    group: 'Maintenance',
    description: 'Failure classification. Drives the recurring-failure analysis in the monthly report.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'sort_order', label: 'Order', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'sort_order', label: 'Sort order', type: 'number', step: 1, default: 0 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'weather-types',
    table: 'weather_types',
    label: 'Weather types',
    singular: 'Weather type',
    group: 'Maintenance',
    description: 'Weather downtime is recorded separately so it never counts against a machine.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'sort_order', label: 'Order', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'sort_order', label: 'Sort order', type: 'number', step: 1, default: 0 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'suppliers',
    table: 'suppliers',
    label: 'Suppliers',
    singular: 'Supplier',
    group: 'Maintenance',
    description: 'Parts and liner suppliers. Lead time feeds the expected-delivery date on shortages.',
    orderBy: 'name',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'contact_name', label: 'Contact' },
      { key: 'phone', label: 'Phone' },
      { key: 'lead_time_days', label: 'Lead days', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'contact_name', label: 'Contact person', type: 'text' },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'lead_time_days', label: 'Typical lead time', type: 'number', step: 1, suffix: 'days' },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  // ------------------------------------------------------------ Fleet (0036)
  {
    key: 'check-items',
    table: 'check_items',
    label: 'Vehicle check items',
    singular: 'Check item',
    group: 'Fleet',
    description:
      'The weekly pre-use checklist (Saturday to Friday), one list per vehicle type. A type with its own rows uses only those; a type with none uses the rows with no type. A defect on a critical row raises a breakdown.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Item' },
      { key: 'machine_type_id', label: 'Vehicle type', lookup: { table: 'machine_types', label: 'name' } },
      { key: 'section', label: 'Section' },
      { key: 'frequency', label: 'How often' },
      { key: 'is_critical', label: 'Critical', type: 'boolean', align: 'center' },
      { key: 'sort_order', label: 'Order', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Item', type: 'text', required: true, help: 'As the driver reads it: Tyres and wheel nuts.' },
      { name: 'machine_type_id', label: 'Only for vehicle type', type: 'select',
        lookup: { table: 'machine_types', label: 'name' }, help: 'Blank: the general list, for types without their own.' },
      { name: 'section', label: 'Section on the sheet', type: 'text',
        help: 'The heading it sits under: Before starting engine, After starting engine, Drum, …' },
      { name: 'frequency', label: 'How often', type: 'select', default: 'daily',
        options: [
          { value: 'daily', label: 'Every day' },
          { value: 'twice_weekly', label: 'Twice a week' },
          { value: 'weekly', label: 'Once a week' },
        ] },
      { name: 'is_critical', label: 'Critical', type: 'boolean', default: false,
        help: 'A defect raises a breakdown: the vehicle should not work until fixed.' },
      { name: 'sort_order', label: 'Order on the sheet', type: 'number', step: 10, default: 100 },
      ACTIVE_FIELD,
    ],
    editableBy: ['maintenance', 'manager', 'admin'],
  },
  // ------------------------------------------------------------ Brick Plant and Pre-cast (0063)
  {
    key: 'brick-products',
    table: 'plant_products',
    label: 'Brick plant products',
    singular: 'Brick plant product',
    group: 'Brick Plant',
    description:
      'The products Brick Plant production, stock and dispatch are recorded against, shared by every branch running it. Branch managers keep this list too. A product with production is made inactive, not removed.',
    orderBy: 'sort_order',
    fixed: { operation: 'brick' },
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'per_pallet', label: 'Per pallet', type: 'number', align: 'right' },
      { key: 'per_m2', label: 'Per m²', type: 'number', decimals: 1, align: 'right' },
      { key: 'curing_days', label: 'Curing days', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'category', label: 'Category', type: 'select', options: [{ value: 'Bricks', label: 'Bricks' }, { value: 'Blocks', label: 'Blocks' }, { value: 'Pavers', label: 'Pavers' }, { value: 'Kerbs', label: 'Kerbs' }, { value: 'Other', label: 'Other' }] },
      { name: 'length_mm', label: 'Length', type: 'number', step: 1, min: 1, suffix: 'mm', section: 'Size' },
      { name: 'width_mm', label: 'Width', type: 'number', step: 1, min: 1, suffix: 'mm' },
      { name: 'height_mm', label: 'Height', type: 'number', step: 1, min: 1, suffix: 'mm' },
      { name: 'mass_kg', label: 'Mass', type: 'number', step: 0.1, min: 0.1, suffix: 'kg' },
      { name: 'strength_mpa', label: 'Strength', type: 'number', step: 1, min: 1, suffix: 'MPa' },
      { name: 'per_pallet', label: 'Pieces per pallet', type: 'number', step: 1, min: 1, section: 'Counting',
        help: 'Production and dispatch can be captured in pallets.' },
      { name: 'per_m2', label: 'Pieces per m²', type: 'number', step: 0.5, min: 0.1, help: 'Pavers: shows m² beside pieces.' },
      { name: 'per_m', label: 'Pieces per metre', type: 'number', step: 0.5, min: 0.1, help: 'Kerbs and fencing: shows metres beside pieces.' },
      { name: 'curing_days', label: 'Curing days', type: 'number', step: 1, min: 0, max: 90, required: true, default: 7,
        help: 'Days after production before stock is available to dispatch.' },
      { name: 'description', label: 'Description', type: 'textarea', full: true },
      { name: 'sort_order', label: 'Order in lists', type: 'number', step: 10, default: 100 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'precast-products',
    table: 'plant_products',
    label: 'Pre-cast products',
    singular: 'Pre-cast product',
    group: 'Pre-cast',
    description:
      'The products Pre-cast production, stock and dispatch are recorded against, shared by every branch running it. Branch managers keep this list too. A product with production is made inactive, not removed.',
    orderBy: 'sort_order',
    fixed: { operation: 'precast' },
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'category', label: 'Category' },
      { key: 'per_pallet', label: 'Per pallet', type: 'number', align: 'right' },
      { key: 'per_m', label: 'Per m', type: 'number', decimals: 1, align: 'right' },
      { key: 'curing_days', label: 'Curing days', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'category', label: 'Category', type: 'select', options: [{ value: 'Fencing panels', label: 'Fencing panels' }, { value: 'Fencing posts', label: 'Fencing posts' }, { value: 'Palisade', label: 'Palisade' }, { value: 'Kerbs', label: 'Kerbs' }, { value: 'Other', label: 'Other' }] },
      { name: 'length_mm', label: 'Length', type: 'number', step: 1, min: 1, suffix: 'mm', section: 'Size' },
      { name: 'width_mm', label: 'Width', type: 'number', step: 1, min: 1, suffix: 'mm' },
      { name: 'height_mm', label: 'Height', type: 'number', step: 1, min: 1, suffix: 'mm' },
      { name: 'mass_kg', label: 'Mass', type: 'number', step: 0.1, min: 0.1, suffix: 'kg' },
      { name: 'strength_mpa', label: 'Strength', type: 'number', step: 1, min: 1, suffix: 'MPa' },
      { name: 'per_pallet', label: 'Pieces per pallet', type: 'number', step: 1, min: 1, section: 'Counting',
        help: 'Production and dispatch can be captured in pallets.' },
      { name: 'per_m2', label: 'Pieces per m²', type: 'number', step: 0.5, min: 0.1, help: 'Pavers: shows m² beside pieces.' },
      { name: 'per_m', label: 'Pieces per metre', type: 'number', step: 0.5, min: 0.1, help: 'Kerbs and fencing: shows metres beside pieces.' },
      { name: 'curing_days', label: 'Curing days', type: 'number', step: 1, min: 0, max: 90, required: true, default: 14,
        help: 'Days after production before stock is available to dispatch.' },
      { name: 'description', label: 'Description', type: 'textarea', full: true },
      { name: 'sort_order', label: 'Order in lists', type: 'number', step: 10, default: 100 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'plant-reasons',
    table: 'plant_reject_reasons',
    label: 'Reject reasons',
    singular: 'Reject reason',
    group: 'Brick Plant',
    description:
      'Why pieces were rejected or broken, for the Brick Plant and Pre-cast. A reason marked "Needs a note" asks for one.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'operation', label: 'For' },
      { key: 'requires_note', label: 'Needs a note', type: 'boolean', align: 'center' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'operation', label: 'For', type: 'select',
        options: [{ value: 'brick', label: 'Brick Plant only' }, { value: 'precast', label: 'Pre-cast only' }],
        help: 'Blank: both.' },
      { name: 'requires_note', label: 'Needs a note', type: 'boolean', default: false },
      { name: 'sort_order', label: 'Order in lists', type: 'number', step: 10, default: 100 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  // ------------------------------------------------------------ Concrete (0035)
  {
    key: 'concrete-mixes',
    table: 'concrete_mixes',
    label: 'Concrete mixes',
    singular: 'Concrete mix',
    group: 'Concrete',
    description:
      'The mixes a delivery is recorded against, shared by every branch. Branch managers can keep this list too.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Name' },
      { key: 'strength_mpa', label: 'MPa', type: 'number', decimals: 0, align: 'right' },
      { key: 'stone_mm', label: 'Stone mm', type: 'number', decimals: 0, align: 'right' },
      { key: 'sort_order', label: 'Order', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Name', type: 'text', required: true, help: 'As the branch says it: 25 MPa / 19 mm.' },
      { name: 'strength_mpa', label: 'Strength', type: 'number', step: 1, min: 1, suffix: 'MPa' },
      { name: 'stone_mm', label: 'Stone size', type: 'number', step: 1, min: 1, suffix: 'mm' },
      { name: 'description', label: 'Description', type: 'textarea', full: true },
      { name: 'sort_order', label: 'Order in lists', type: 'number', step: 10, default: 100 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'concrete-customers',
    table: 'concrete_customers',
    label: 'Concrete customers',
    singular: 'Customer',
    group: 'Concrete',
    description:
      'Who the concrete is delivered to, shared by every branch. Branch staff can also add a customer while capturing a delivery.',
    orderBy: 'name',
    columns: [
      { key: 'name', label: 'Name' },
      { key: 'code', label: 'Account' },
      { key: 'contact_name', label: 'Contact' },
      { key: 'phone', label: 'Phone' },
      { key: 'site_id', label: 'Added by', lookup: { table: 'sites', label: 'name' } },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'code', label: 'Account number', type: 'text', help: 'Optional: the account number in the accounting system.' },
      { name: 'contact_name', label: 'Contact person', type: 'text' },
      { name: 'phone', label: 'Phone', type: 'text' },
      { name: 'email', label: 'Email', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'textarea', full: true },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
  {
    key: 'disposal-reasons',
    table: 'concrete_disposal_reasons',
    label: 'Disposal reasons',
    singular: 'Disposal reason',
    group: 'Concrete',
    description:
      'Why concrete was disposed of or rejected. A reason marked "needs a note" makes the capturer explain it.',
    orderBy: 'sort_order',
    columns: [
      { key: 'code', label: 'Code' },
      { key: 'name', label: 'Reason' },
      { key: 'requires_note', label: 'Needs a note', type: 'boolean', align: 'center' },
      { key: 'sort_order', label: 'Order', type: 'number', align: 'right' },
      { key: 'is_active', label: 'Active', type: 'boolean', align: 'center' },
    ],
    fields: [
      CODE_FIELD,
      { name: 'name', label: 'Reason', type: 'text', required: true },
      { name: 'requires_note', label: 'Needs a note', type: 'boolean', default: false,
        help: 'The capturer must say what happened (for example for "Other").' },
      { name: 'sort_order', label: 'Order in lists', type: 'number', step: 10, default: 100 },
      ACTIVE_FIELD,
    ],
    editableBy: MANAGER,
  },
];

export const ENTITY_GROUPS = ['Fleet', 'Daily diary', 'Material & places', 'Maintenance', 'Concrete', 'Brick Plant', 'Pre-cast'];

export function findEntity(key: string): EntityDef | undefined {
  return ENTITIES.find((e) => e.key === key);
}
